import { Fragment } from 'react'
import type { ReactNode } from 'react'
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react'
import { useTranslation } from 'next-i18next'
import { twMerge } from 'tailwind-merge'
import useDomainAssets from 'shared/hooks/use-domain-assets'
import useUser from 'shared/hooks/use-user'
import CloseThinIcon from 'shared/icons/close-thin-icon'
import { PostAttachmentsProvider } from 'modules/attachments/components/context'
import { PostAnnounceSkeleton } from 'modules/community/components/post-announce/components/post-announce-skeleton'
import { PostView } from 'modules/community/components/post/components/post-view'
import { shouldHideUnderModerationContent } from 'modules/community/components/under-moderation-placeholder'
import type { PostInterface } from 'modules/community/types/post-interface'
import { useBrand } from 'modules/white-label/contexts/brand-context'
import type { CommentsHighlightContext } from './types'

export interface BaseModalProps {
  className?: string
  opened: boolean
  onClose: () => void
  deletePost?: () => Promise<void>
  deletePostAndBanUser?: (removeRecentActivitiesChecked: boolean) => Promise<void>
  post: PostInterface
  handleLikeClick?: () => void
  handleDeclinePostAndBanUser?: (removeRecentActivitiesChecked: boolean) => Promise<void>
  commentsHighlightContext?: CommentsHighlightContext
}

function PostModal({
  className,
  onClose,
  opened,
  post,
  handleLikeClick,
  deletePost,
  deletePostAndBanUser,
  handleDeclinePostAndBanUser,
  commentsHighlightContext,
}: BaseModalProps): ReactNode {
  const { t } = useTranslation()
  const { domainAssets } = useDomainAssets()
  const { user, loading: isUserLoading } = useUser()
  const hideContent = shouldHideUnderModerationContent(post.underModeration, user)

  const { brand } = useBrand()

  if (!user || isUserLoading) {
    return <PostAnnounceSkeleton />
  }

  return (
    <PostAttachmentsProvider attachments={post.attachments}>
      <Transition show={opened} as={Fragment}>
        <Dialog
          className="fixed z-20"
          open={opened}
          onClose={() => {
            /* empty */
          }}
          onClick={e => {
            if (e.target === e.currentTarget) onClose()
          }}
        >
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/60 transition-opacity" />
          </TransitionChild>

          <div
            data-scrollable="comments-container"
            data-testid="comments-container"
            className="fixed inset-0 z-10 overflow-y-auto"
          >
            <div className="flex min-h-full w-full items-end justify-center text-center sm:items-center">
              <div className="relative w-full max-w-210">
                <button
                  className="absolute top-5 right-0 z-1001 mt-11 ml-3.75 hidden items-center justify-center lg:flex xl:flex 2xl:flex"
                  onClick={onClose}
                >
                  <CloseThinIcon className="fill-background-secondary" />
                </button>
                <TransitionChild
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                  enterTo="opacity-100 translate-y-0 sm:scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                  leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                  <DialogPanel
                    className={twMerge(
                      'relative min-h-screen w-full max-w-200 bg-background-secondary text-left shadow-xl transition-all md:mt-16 md:min-h-fit md:rounded-lg',
                      className,
                    )}
                  >
                    <div className="sticky top-0 z-100 flex h-12 w-full items-center border-b border-text-secondary/30 bg-background-secondary px-4 py-2 md:hidden">
                      <div className="flex w-full min-w-0">
                        <div className="flex min-w-0 items-center gap-2 text-text-primary">
                          <button className="flex items-center justify-center" onClick={onClose}>
                            <CloseThinIcon className="fill-text-secondary" />
                          </button>
                          <img
                            src={domainAssets?.logoUrl || brand.logo?.path}
                            alt="logo"
                            className="size-6 rounded-full"
                            width={24}
                            height={24}
                          />
                          <p className="truncate text-sm font-bold">
                            {hideContent
                              ? t('community.moderation.content.post_hidden')
                              : post.title}
                          </p>
                        </div>
                      </div>
                    </div>
                    <PostView
                      post={post}
                      handleDeletePost={deletePost}
                      handleLikeClick={handleLikeClick}
                      deletePostAndBanUser={deletePostAndBanUser}
                      handleDeclinePostAndBanUser={handleDeclinePostAndBanUser}
                      isLoading={false}
                      commentsHighlightContext={commentsHighlightContext}
                    />
                  </DialogPanel>
                </TransitionChild>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition>
    </PostAttachmentsProvider>
  )
}

export default PostModal
